import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ChevronLeft, Eye, Loader2 } from "lucide-react";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { Preview } from "~/components/events/preview";
import { PreviewDialog } from "~/components/events/preview-dialog";
import { BasicsStep } from "~/components/events/steps/basics";
import type { CreateEventFormData } from "~/components/events/utils";
import { createEventFormSchema } from "~/components/events/utils";
import { Button } from "~/components/ui/button";
import { eventApi } from "~/lib/api/event";
import { cn } from "~/lib/utils";

function CreateEventPage() {
	const [previewOpen, setPreviewOpen] = useState(false);
	const form = useForm<CreateEventFormData>({
		resolver: zodResolver(createEventFormSchema),
		defaultValues: {
			title: "",
			allowPlusOne: false,
			guestListVisibility: true,
		},
	});

	const navigate = useNavigate();
	const createEvent = useMutation({
		mutationFn: async (data: CreateEventFormData) => {
			const response = await eventApi.createEvent(data);
			return response.data;
		},
		onError: (error) => {
			toast.error("Failed to create event", {
				// TODO: better error message
				description: error.message,
			});
		},
	});

	const handleSubmit = form.handleSubmit(
		async (data) => {
			const startDate = new Date(data.startDate);
			startDate.setHours(
				Number(data.startTime.split(":")[0]),
				Number(data.startTime.split(":")[1]),
				0,
				0,
			);

			// Cleanup object URL if it exists
			if (data.coverImageUrl) {
				URL.revokeObjectURL(data.coverImageUrl);
			}

			const event = await createEvent.mutateAsync({
				...data,
				startDate: startDate.toISOString(),
			});

			// Show success message with event title and action buttons
			toast.success(`${event.title} created successfully!`, {
				description: "Your event is ready to share with guests.",
				action: {
					label: "View Event",
					onClick: () => navigate({ to: `/event/${event.slug}` }),
				},
				duration: 5000, // Give users more time to click the action
			});

			// Add a small delay before redirect to ensure toast is visible
			setTimeout(() => {
				navigate({ to: `/event/${event.slug}` });
			}, 1000);
		},
		() => {
			toast.error("Please fix the highlighted errors before continuing");
		},
	);

	return (
		<FormProvider {...form}>
			<div className="min-h-screen bg-black text-primary-foreground flex flex-col md:flex-row">
				{/* Form Section - Full width on mobile, 55% on desktop */}
				<div className="w-full md:w-[55%] flex-shrink-0 flex flex-col justify-between p-4 md:p-6 relative h-screen">
					{/* Back Link */}
					<div className="flex items-center justify-between mb-4 md:mb-6">
						<Link
							to="/"
							className={cn(
								"inline-flex items-center text-white/70 hover:text-white group",
								createEvent.isPending && "pointer-events-none opacity-50",
							)}
							aria-label="Back to homepage"
						>
							<ChevronLeft className="mr-2 h-5 w-5 transition-transform group-hover:-translate-x-1" />
							Back to Dashboard
						</Link>
						<Button
							variant="default"
							size="icon"
							onClick={() => setPreviewOpen(true)}
							className="ml-auto md:hidden bg-accent hover:bg-accent/90 text-accent-foreground"
						>
							<Eye className="h-6 w-6" />
						</Button>
					</div>

					{/* Scrollable Content Area */}
					<div className="flex-1 overflow-y-auto">
						{/* Header */}
						<div className="mb-6 md:mb-8">
							<h1 className="text-3xl md:text-4xl font-bold text-primary-foreground bg-gradient-to-br from-white to-accent/80 bg-clip-text font-heading tracking-wide">
								Create New Event ✨
							</h1>
							<p className="mt-2 text-base md:text-lg text-secondary">
								Fill in the details to plan your perfect gathering
							</p>
						</div>

						{/* Form Content */}
						<div
							className={cn(
								"rounded-xl border border-white/10 backdrop-blur-xl p-4 md:p-6 space-y-4 [&>*:nth-child(2)]:md:!mt-0",
								createEvent.isPending && "opacity-50",
							)}
						>
							<BasicsStep disabled={createEvent.isPending} />
						</div>
					</div>

					{/* Fixed Footer */}
					<div className="bg-transparent backdrop-blur-sm w-full pt-4 md:pt-6">
						<Button
							className={cn(
								"bg-accent text-accent-foreground hover:bg-accent/90 w-full",
								createEvent.isPending && "opacity-80",
							)}
							onClick={handleSubmit}
							disabled={createEvent.isPending}
						>
							{createEvent.isPending ? (
								<Loader2 className="w-4 h-4 animate-spin mr-2" />
							) : null}
							Create Event
						</Button>
					</div>
				</div>

				{/* Only enabled on mobile */}
				<PreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} />

				{/* Preview Section - Hidden on mobile */}
				<div className="hidden flex-1 border-l border-white/10 h-screen md:flex flex-col">
					<div className="flex items-center justify-between p-6 border-b border-white/10">
						<h2 className="text-lg font-semibold text-white">Event Preview</h2>
					</div>
					<div className="flex-1 overflow-y-auto">
						<Preview />
					</div>
				</div>
			</div>
		</FormProvider>
	);
}

export const Route = createFileRoute("/_protected/event/create")({
	component: CreateEventPage,
});
